import Color from "@tiptap/extension-color";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import type { EditorView } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";

import { ParagraphResourceMeta } from "../paragraph-resource-meta/paragraph-resource-meta";
import { ResourceSlotsClipboard } from "../paragraph-resource-meta/resource-slots-clipboard";
import { ResourceSlotsPrune } from "../paragraph-resource-meta/resource-slots-prune";
import { ResourceTokenHighlight } from "../resource-token/highlight-extension";
import { ResourceSuggestionPlugin } from "../suggestion/suggestion-plugin";
import type { ResourceOptionsForPruneRef } from "../paragraph-resource-meta/resource-slots-prune";
import type { ReactionResourceOption, SuggestionState } from "../suggestion/suggestion-types";

export interface FormulaExtensionsOptions {
  onSuggestionUpdate?: (state: SuggestionState | null) => void;
  onSuggestionKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean;
  getContainerRect?: () => DOMRect | null;
  /** Used by {@link ResourceSlotsPrune} to match `screenField` slots to `{label}` in text. */
  resourceOptionsForPrune?: ReactionResourceOption[];
  resourceOptionsRef?: ResourceOptionsForPruneRef;
  preserveResourceSlotsOnClipboard?: boolean;
  onPastedResourceSlots?: (slots: string[]) => void;
}

export function createFormulaEditorExtensions(
	options: FormulaExtensionsOptions
) {
	const extensions = [
		// basic tiptap extension configuration
		StarterKit.configure({
			heading: false,
			hardBreak: false,
			paragraph: false,
		}),
		ParagraphResourceMeta.configure({
			HTMLAttributes: {
				class: "tiptap-paragraph",
			},
		}),
		ResourceSlotsPrune.configure({
			resourceOptionsForPrune: options.resourceOptionsForPrune ?? [],
			resourceOptionsRef: options.resourceOptionsRef,
		}),
		Underline,
		Highlight.configure({
			multicolor: true,
			HTMLAttributes: { class: "tiptap-highlight" },
		}),
		TextStyle,
		Color,
		TextAlign.configure({
			types: ["paragraph", "heading"],
			alignments: ["left", "center", "right", "justify"],
		}),
		Link.configure({
			openOnClick: false,
			HTMLAttributes: {
				class: "tiptap-link",
			},
		}),
		Heading.configure({ levels: [1, 2, 3] }),
		Superscript,
		Subscript,
		HardBreak.configure({
			keepMarks: true,
			HTMLAttributes: { class: "line-break" },
		}),
		// adds highlight extension so tokens get .resource-token class
		ResourceTokenHighlight,
		// suggestion extension, should probably name it that instead of Plugin
		ResourceSuggestionPlugin.configure({
			onUpdate: options.onSuggestionUpdate,
			onKeyDown: options.onSuggestionKeyDown,
			getContainerRect: options.getContainerRect,
		}),
	];

	if (options.preserveResourceSlotsOnClipboard) {
		extensions.push(
			ResourceSlotsClipboard.configure({
				enabled: true,
				onPastedSlots: options.onPastedResourceSlots,
			}),
		);
	}

	return extensions;
}
